import {Injectable} from '@angular/core';
import {AuthService} from '../auth/auth.service';

/**
 * Persists the filters used on a list page, so they are restored
 * as defaults the next time the page is visited.
 *
 * Keys are namespaced by connected user, so filters never leak from
 * one account to another when switching users on the same browser.
 */
@Injectable({
  providedIn: 'root'
})
export class FilterStorageService {

  private static readonly USER_KEY = 'filters:user';

  constructor(private auth: AuthService) {
    // Forget the current user on logout, so nothing is saved or restored
    // until the next account is known
    this.auth.loginEvent.subscribe((logged: boolean) => {
      if (!logged) {
        localStorage.removeItem(FilterStorageService.USER_KEY);
      }
    });
  }

  save(page: string, filters: any): void {
    const key = this.getKey(page);
    if (key === null) {
      return;
    }

    try {
      localStorage.setItem(key, JSON.stringify(filters));
    } catch {
      // Storage full or unavailable: filters simply won't be restored
    }
  }

  load(page: string): any {
    const key = this.getKey(page);
    if (key === null) {
      return null;
    }

    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }

  private getKey(page: string): string | null {
    const userId = this.getUserId();

    return userId === null ? null : 'filters:' + userId + ':' + page;
  }

  private getUserId(): string | null {
    const config = this.auth.getConfig();
    const id = config?.affiliateUserId ?? config?.partner?.id ?? null;

    if (id !== null && id !== undefined) {
      localStorage.setItem(FilterStorageService.USER_KEY, String(id));
      return String(id);
    }

    // Config not loaded yet (page refresh): reuse the last known user of this browser
    return localStorage.getItem(FilterStorageService.USER_KEY);
  }
}
